import { useCallback, useEffect, useRef, useState } from "react";

import { loadFromLocalStorage, saveToLocalStorage } from "../localStorage";

// Space kept between the panel and the edge of the window.
const VIEWPORT_MARGIN = 64;
// How much one arrow key press resizes the panel.
const KEYBOARD_STEP = 24;

/**
 * Drag-to-resize for a panel that only changes height, the way a window is
 * stretched by its bottom edge. Pointer capture keeps the drag alive while the
 * cursor travels over an <iframe>, which would otherwise swallow the events.
 */
const useVerticalResize = ({ defaultHeight, minHeight = 320, storageKey }) => {
  const clamp = useCallback(
    (value) => {
      const maxHeight = Math.max(minHeight, window.innerHeight - VIEWPORT_MARGIN);
      return Math.round(Math.min(Math.max(value, minHeight), maxHeight));
    },
    [minHeight],
  );

  const [height, setHeight] = useState(() => {
    const stored = storageKey ? Number(loadFromLocalStorage(storageKey)) : NaN;
    return clamp(Number.isFinite(stored) && stored > 0 ? stored : defaultHeight);
  });
  const [resizing, setResizing] = useState(false);

  // The pointer handlers need the live height without re-binding on each move.
  const heightRef = useRef(height);
  const dragRef = useRef(null);

  const applyHeight = useCallback(
    (value) => {
      const next = clamp(value);
      heightRef.current = next;
      setHeight(next);
      return next;
    },
    [clamp],
  );

  const persist = useCallback(() => {
    if (storageKey) saveToLocalStorage(storageKey, heightRef.current);
  }, [storageKey]);

  // Shrinking the browser window must not leave the panel taller than the view.
  useEffect(() => {
    const windowResizeHandler = () => applyHeight(heightRef.current);

    window.addEventListener("resize", windowResizeHandler);
    return () => window.removeEventListener("resize", windowResizeHandler);
  }, [applyHeight]);

  const pointerDownHandler = (e) => {
    // Only the primary button starts a drag.
    if (e.button !== 0) return;

    e.preventDefault();
    dragRef.current = { startY: e.clientY, startHeight: heightRef.current };
    e.currentTarget.setPointerCapture(e.pointerId);
    setResizing(true);
  };

  const pointerMoveHandler = (e) => {
    if (!dragRef.current) return;

    applyHeight(dragRef.current.startHeight + (e.clientY - dragRef.current.startY));
  };

  const pointerUpHandler = (e) => {
    if (!dragRef.current) return;

    dragRef.current = null;
    if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    setResizing(false);
    persist();
  };

  const keyDownHandler = (e) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      applyHeight(heightRef.current + (e.key === "ArrowDown" ? KEYBOARD_STEP : -KEYBOARD_STEP));
      persist();
    }
  };

  // Double click on the handle restores the height the panel opens with.
  const doubleClickHandler = () => {
    applyHeight(defaultHeight);
    persist();
  };

  return {
    height,
    resizing,
    handleProps: {
      onPointerDown: pointerDownHandler,
      onPointerMove: pointerMoveHandler,
      onPointerUp: pointerUpHandler,
      onPointerCancel: pointerUpHandler,
      onKeyDown: keyDownHandler,
      onDoubleClick: doubleClickHandler,
      role: "separator",
      "aria-orientation": "horizontal",
      "aria-valuenow": height,
      "aria-valuemin": minHeight,
      tabIndex: 0,
    },
  };
};

export default useVerticalResize;
